<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h1 class="page-title">{{ t('property_list') }}</h1>
                <p class="page-subtitle">{{ t('property_list_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="draft">{{ t('draft') }}</option>
                        <option value="live">{{ t('live') }}</option>
                        <option value="stopped">{{ t('stopped') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="load" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button type="button" class="tool-btn tool-btn-success" @click="openCreate" :disabled="loading">{{ t('create') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search') + '...'" @input="debouncedSearch">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.ref_no">
                                <input v-if="showFilters" v-model="colFilters.refNo" type="text" class="th-input" :placeholder="t('ref_no')">
                                <span v-else>{{ t('ref_no') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer">
                                <input v-if="showFilters" v-model="colFilters.customer" type="text" class="th-input" :placeholder="t('customer')">
                                <span v-else>{{ t('customer') }}</span>
                            </th>
                            <th v-if="visibleColumns.property_type">
                                <input v-if="showFilters" v-model="colFilters.propertyType" type="text" class="th-input" :placeholder="t('property_type')">
                                <span v-else>{{ t('property_type') }}</span>
                            </th>
                            <th v-if="visibleColumns.unit">
                                <input v-if="showFilters" v-model="colFilters.unit" type="text" class="th-input" :placeholder="t('unit')">
                                <span v-else>{{ t('unit') }}</span>
                            </th>
                            <th v-if="visibleColumns.rent" class="col-rent">
                                <input v-if="showFilters" v-model="colFilters.rent" type="text" class="th-input th-input-right" :placeholder="t('rent')">
                                <span v-else>{{ t('rent') }}</span>
                            </th>
                            <th v-if="visibleColumns.district">
                                <input v-if="showFilters" v-model="colFilters.district" type="text" class="th-input" :placeholder="t('district')">
                                <span v-else>{{ t('district') }}</span>
                            </th>
                            <th v-if="visibleColumns.status">
                                <select v-if="showFilters" v-model="colFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="draft">{{ t('draft') }}</option>
                                    <option value="live">{{ t('live') }}</option>
                                    <option value="stopped">{{ t('stopped') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredItems" v-if="!loading" :key="row.ToLetNoticeID">
                            <td v-if="visibleColumns.ref_no" class="mono">{{ row.ToLetRefNo || '-' }}</td>
                            <td v-if="visibleColumns.customer">
                                <div class="cell-main">{{ row.CustomerName || '-' }}</div>
                                <div class="cell-sub mono">{{ row.MobileNo || '-' }}</div>
                            </td>
                            <td v-if="visibleColumns.property_type">{{ row.PropertyTypeName || '-' }}</td>
                            <td v-if="visibleColumns.unit">{{ row.Unit || '-' }}</td>
                            <td v-if="visibleColumns.rent" class="mono col-rent">{{ row.Rent ?? '-' }}</td>
                            <td v-if="visibleColumns.district">{{ districtLabel(row) }}</td>
                            <td v-if="visibleColumns.status">
                                <span :class="['badge', badgeClass(row.Status)]">{{ statusLabel(row.Status) }}</span>
                            </td>
                            <td v-if="visibleColumns.actions" class="actions">
                                <button type="button" class="btn btn-small" @click="openView(row.ToLetNoticeID)" :disabled="busyIds[row.ToLetNoticeID]" :title="t('view')" aria-label="View">
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                        <path d="M1.66667 10C1.66667 10 4.58333 4.16667 10 4.16667C15.4167 4.16667 18.3333 10 18.3333 10C18.3333 10 15.4167 15.8333 10 15.8333C4.58333 15.8333 1.66667 10 1.66667 10Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <button type="button" class="btn btn-small btn-secondary" @click="openEdit(row.ToLetNoticeID)" :disabled="busyIds[row.ToLetNoticeID]" :title="t('edit')" aria-label="Edit">
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                        <path d="M14.1667 2.5C14.3856 2.28113 14.6454 2.10753 14.9314 1.98908C15.2175 1.87062 15.524 1.80963 15.8333 1.80963C16.1427 1.80963 16.4492 1.87062 16.7352 1.98908C17.0213 2.10753 17.2811 2.28113 17.5 2.5C17.7189 2.71888 17.8925 2.97872 18.0109 3.26476C18.1294 3.5508 18.1904 3.85737 18.1904 4.16667C18.1904 4.47596 18.1294 4.78253 18.0109 5.06857C17.8925 5.35461 17.7189 5.61445 17.5 5.83333L6.25 17.0833L1.66667 18.3333L2.91667 13.75L14.1667 2.5Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <button type="button" class="btn btn-small btn-secondary" @click="toggleStatus(row)" :disabled="busyIds[row.ToLetNoticeID]" :title="nextStatusLabel(row.Status)" aria-label="Toggle status">
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                        <path d="M1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C13.1522 1.66667 15.8961 3.41609 17.3281 6.0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C6.84778 18.3333 4.10391 16.5839 2.67188 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M16.6667 6V1.66667H12.3333" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M3.33333 14V18.3333H7.66667" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <button type="button" class="btn btn-small btn-danger" @click="remove(row.ToLetNoticeID)" :disabled="busyIds[row.ToLetNoticeID]" :title="t('delete')" aria-label="Delete">
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                        <path d="M2.5 5H17.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                        <path d="M8.33333 8.33333V14.1667" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                        <path d="M11.6667 8.33333V14.1667" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                        <path d="M4.16667 5L5 16.6667C5 17.1269 5.3731 17.5 5.83333 17.5H14.1667C14.6269 17.5 15 17.1269 15 16.6667L15.8333 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M7.5 5V3.33333C7.5 2.8731 7.8731 2.5 8.33333 2.5H11.6667C12.1269 2.5 12.5 2.8731 12.5 3.33333V5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                        <tr v-if="!loading && !filteredItems.length">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">
                    <div class="pager-info">{{ showingText }}</div>
                </div>
                <div class="pager-right">
                    <button type="button" class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button type="button" class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button
                        v-for="p in pageButtons"
                        :key="p"
                        type="button"
                        class="pager-num"
                        :class="{ active: p === page }"
                        @click="goPage(p)"
                        :disabled="loading"
                    >
                        {{ p }}
                    </button>
                    <button type="button" class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button type="button" class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="showViewModal" class="app-modal-overlay" @click.self="closeView">
                <div class="app-modal">
                    <div class="app-modal-header">
                        <h3>{{ t('property_details') }}</h3>
                        <button class="app-modal-close" @click="closeView">×</button>
                    </div>
                    <div class="app-modal-body">
                        <div v-if="viewData" class="details-grid">
                            <div class="detail">
                                <div class="detail-label">{{ t('ref_no') }}</div>
                                <div class="detail-value mono">{{ viewData.notice?.ToLetRefNo || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('customer') }}</div>
                                <div class="detail-value">{{ viewData.notice?.CustomerName || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('property_type') }}</div>
                                <div class="detail-value">{{ viewData.notice?.PropertyTypeName || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('status') }}</div>
                                <div class="detail-value">{{ statusLabel(viewData.notice?.Status) }}</div>
                            </div>
                            <div class="detail span-2">
                                <div class="detail-label">{{ t('summary') }}</div>
                                <div class="detail-value">{{ viewData.notice?.DisplaySummary || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('publicity_start') }}</div>
                                <div class="detail-value">{{ viewData.notice?.PublicityStartDate || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('publicity_end') }}</div>
                                <div class="detail-value">{{ viewData.notice?.PublicityEndDate || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('available_from') }}</div>
                                <div class="detail-value">{{ viewData.notice?.PropertyAvailableDate || '-' }}</div>
                            </div>
                            <div class="detail">
                                <div class="detail-label">{{ t('bill') }}</div>
                                <div class="detail-value">{{ t('id') }}: {{ viewData.notice?.BillID || '-' }}, {{ t('status') }}: {{ viewData.notice?.BillStatus || '-' }}</div>
                            </div>
                            <div class="detail span-2">
                                <div class="detail-label">{{ t('late_payment') }}</div>
                                <div class="detail-value">
                                    {{ t('allowed') }}: {{ viewData.notice?.IsLatePaymentAllowed ? t('yes') : t('no') }},
                                    {{ t('due') }}: {{ viewData.notice?.PaymentDueDate || '-' }},
                                    {{ t('fee') }}: {{ viewData.notice?.LateFee || '-' }} <span v-if="viewData.notice?.LateFeeType">({{ viewData.notice?.LateFeeType }})</span>
                                </div>
                            </div>
                            <div class="detail span-2">
                                <div class="detail-label">{{ t('features') }}</div>
                                <div class="detail-value">
                                    <pre v-if="viewFeaturesText" class="json-box">{{ viewFeaturesText }}</pre>
                                    <span v-else>-</span>
                                </div>
                            </div>
                            <div class="detail span-2">
                                <div class="detail-label">{{ t('preferences') }}</div>
                                <div class="detail-value">
                                    <pre v-if="viewPreferencesText" class="json-box">{{ viewPreferencesText }}</pre>
                                    <span v-else>-</span>
                                </div>
                            </div>
                            <div class="detail span-2">
                                <div class="detail-label">{{ t('use_types') }}</div>
                                <div class="detail-value">
                                    <span v-if="!viewData.use_types?.length">-</span>
                                    <span v-else>
                                        <span v-for="(u, idx) in viewData.use_types" :key="u.PropertyUseTypeID">
                                            {{ useTypeLabel(u) }}<span v-if="idx < viewData.use_types.length - 1">, </span>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="loading">{{ t('loading') }}</div>
                    </div>
                    <div class="app-modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeView">{{ t('close') }}</button>
                    </div>
                </div>
            </div>

            <div v-if="showFormModal" class="app-modal-overlay" @click.self="closeForm">
                <div class="app-modal app-modal-large">
                    <div class="app-modal-header">
                        <h3>{{ formMode === 'create' ? t('create_property') : t('edit_property') }}</h3>
                        <button class="app-modal-close" @click="closeForm">×</button>
                    </div>
                    <div class="app-modal-body">
                        <div class="step-tabs">
                            <button type="button" :class="['step-tab', activeTab === 'info' && 'active']" @click="setTab('info')">{{ t('info') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'location' && 'active']" @click="setTab('location')">{{ t('location') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'features' && 'active']" @click="setTab('features')">{{ t('features') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'preferences' && 'active']" @click="setTab('preferences')">{{ t('preferences') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'media' && 'active']" @click="setTab('media')">{{ t('media') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'billing' && 'active']" @click="setTab('billing')">{{ t('billing') }}</button>
                            <button type="button" :class="['step-tab', activeTab === 'late' && 'active']" @click="setTab('late')">{{ t('late_payment') }}</button>
                        </div>

                        <div v-if="activeTab === 'info'" class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('customer_id') }}</label>
                                <input v-model="form.customer_id" type="number" min="1" class="form-input" :disabled="formMode === 'edit'">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('property_type') }}</label>
                                <select v-model="form.property_type_id" class="form-input" @change="loadUseTypes">
                                    <option value="">{{ t('select') }}</option>
                                    <option v-for="pt in propertyTypes" :key="pt.PropertyTypeID" :value="String(pt.PropertyTypeID)">{{ propertyTypeLabel(pt) }}</option>
                                </select>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('summary') }}</label>
                                <input v-model="form.display_summary" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('rent') }}</label>
                                <input v-model="form.rent" type="number" min="0" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('unit') }}</label>
                                <input v-model="form.unit" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('unit_size') }}</label>
                                <input v-model="form.unit_size" type="number" min="0" step="0.01" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('status') }}</label>
                                <select v-model="form.status" class="form-input">
                                    <option value="draft">{{ t('draft') }}</option>
                                    <option value="live">{{ t('live') }}</option>
                                    <option value="stopped">{{ t('stopped') }}</option>
                                </select>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('use_types') }}</label>
                                <select v-model="form.property_use_type_ids" class="form-input" multiple>
                                    <option v-for="u in propertyUseTypes" :key="u.PropertyUseTypeID" :value="u.PropertyUseTypeID">{{ propertyUseTypeLabel(u) }}</option>
                                </select>
                            </div>
                        </div>

                        <div v-else-if="activeTab === 'location'" class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('district') }}</label>
                                <select v-model="form.district_id" class="form-input" @change="onDistrictChange">
                                    <option value="">{{ t('select') }}</option>
                                    <option v-for="d in districts" :key="d.District_ID" :value="String(d.District_ID)">{{ districtOptionLabel(d) }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('thana') }}</label>
                                <select v-model="form.thana_id" class="form-input" @change="onThanaChange">
                                    <option value="">{{ t('select') }}</option>
                                    <option v-for="tItem in thanas" :key="tItem.ThanaID" :value="String(tItem.ThanaID)">{{ thanaLabel(tItem) }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('area') }}</label>
                                <select v-model="form.area_id" class="form-input">
                                    <option value="">{{ t('select') }}</option>
                                    <option v-for="a in areas" :key="a.AreaID" :value="String(a.AreaID)">{{ areaLabel(a) }}</option>
                                </select>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('address') }}</label>
                                <input v-model="form.address" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('latlong') }}</label>
                                <input v-model="form.latlong" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('show_address') }}</label>
                                <select v-model="form.show_address_to_public" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                        </div>

                        <div v-else-if="activeTab === 'features'" class="form-grid">
                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('rooms_spaces') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('total_room') }}</label>
                                        <input v-model="form.features.rooms_spaces.total_room" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('balconies') }}</label>
                                        <input v-model="form.features.rooms_spaces.balconies" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('dining_room') }}</label>
                                        <select v-model="form.features.rooms_spaces.dining_room" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('dining_room_size') }}</label>
                                        <input v-model="form.features.rooms_spaces.dining_room_size" type="text" class="form-input" :placeholder="t('optional_placeholder')">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('drawing_room') }}</label>
                                        <select v-model="form.features.rooms_spaces.drawing_room" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('drawing_room_size') }}</label>
                                        <input v-model="form.features.rooms_spaces.drawing_room_size" type="text" class="form-input" :placeholder="t('optional_placeholder')">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('attached_dining_drawing') }}</label>
                                        <select v-model="form.features.rooms_spaces.attached_dining_drawing" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('attached_dining_drawing_size') }}</label>
                                        <input v-model="form.features.rooms_spaces.attached_dining_drawing_size" type="text" class="form-input" :placeholder="t('optional_placeholder')">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('floor_type') }}</label>
                                        <input v-model="form.features.rooms_spaces.floor_type" type="text" class="form-input" :placeholder="t('floor_type_placeholder')">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('floor_level') }}</label>
                                        <input v-model="form.features.rooms_spaces.floor_level" type="text" class="form-input" :placeholder="t('floor_level_placeholder')">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('total_floor_in_building') }}</label>
                                        <input v-model="form.features.rooms_spaces.total_floor_in_building" type="number" min="0" class="form-input">
                                    </div>
                                </div>
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('kitchen_features') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('has_kitchen') }}</label>
                                        <select v-model="form.features.kitchen.has_kitchen" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group span-2">
                                        <label class="form-label">{{ t('kitchen_description') }}</label>
                                        <textarea v-model="form.features.kitchen.kitchen_description" class="form-input" rows="2"></textarea>
                                    </div>
                                    <div class="form-group span-2">
                                        <label class="form-label">{{ t('gas_connection_type') }}</label>
                                        <input v-model="form.features.kitchen.gas_connection_type" type="text" class="form-input">
                                    </div>
                                </div>
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('bathroom_features') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('attached_bath') }}</label>
                                        <input v-model="form.features.bathroom.attached_bath" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('common_bath') }}</label>
                                        <input v-model="form.features.bathroom.common_bath" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('high_commode') }}</label>
                                        <input v-model="form.features.bathroom.high_commode" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('low_commode') }}</label>
                                        <input v-model="form.features.bathroom.low_commode" type="number" min="0" class="form-input">
                                    </div>
                                    <div class="form-group span-2">
                                        <label class="form-label">{{ t('other_facilities') }}</label>
                                        <textarea v-model="form.features.bathroom.other_facilities" class="form-input" rows="2"></textarea>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('utility_services') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('electricity') }}</label>
                                        <select v-model="form.features.utilities_services.electricity" class="form-input">
                                            <option value="">{{ t('select') }}</option>
                                            <option value="dedicated">{{ t('dedicated') }}</option>
                                            <option value="shared">{{ t('shared') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('water_wasa') }}</label>
                                        <select v-model="form.features.utilities_services.water_wasa" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('water_deep_tubewell') }}</label>
                                        <select v-model="form.features.utilities_services.water_deep_tubewell" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('lift') }}</label>
                                        <select v-model="form.features.utilities_services.lift" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('parking_space') }}</label>
                                        <select v-model="form.features.utilities_services.parking_space" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('community_space') }}</label>
                                        <select v-model="form.features.utilities_services.community_space" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('generator_backup_power') }}</label>
                                        <select v-model="form.features.utilities_services.generator_backup_power" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('internet_broadband') }}</label>
                                        <select v-model="form.features.utilities_services.internet_broadband" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('security_safety') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('security_guard') }}</label>
                                        <select v-model="form.features.security_safety.security_guard" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('cctv_surveillance') }}</label>
                                        <select v-model="form.features.security_safety.cctv" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('intercom_system') }}</label>
                                        <select v-model="form.features.security_safety.intercom" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('fire_safety_system') }}</label>
                                        <select v-model="form.features.security_safety.fire_safety" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="activeTab === 'preferences'" class="form-grid">
                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('furnishing_status') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('status') }}</label>
                                        <select v-model="form.preferences.furnishing_status" class="form-input">
                                            <option value="">{{ t('select') }}</option>
                                            <option value="unfurnished">{{ t('unfurnished') }}</option>
                                            <option value="furnished">{{ t('furnished') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group span-2">
                                        <label class="form-label">{{ t('furnished_details') }}</label>
                                        <textarea v-model="form.preferences.furnished_details" class="form-input" rows="3" :placeholder="t('optional_placeholder')"></textarea>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="form-label">{{ t('residential_preference') }}</div>
                                <div class="form-grid inner-grid">
                                    <div class="form-group">
                                        <label class="form-label">{{ t('family') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.family" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('small_family') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.small_family" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('bachelor') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.bachelor" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('student_male') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.student_male" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('student_female') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.student_female" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('service_holder') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.service_holder" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ t('any') }}</label>
                                        <select v-model="form.preferences.preferred_tenant.any" class="form-input">
                                            <option :value="true">{{ t('yes') }}</option>
                                            <option :value="false">{{ t('no') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="activeTab === 'media'" class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('allow_foreigner') }}</label>
                                <select v-model="form.is_foreigner_allow" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('allow_show_mobile') }}</label>
                                <select v-model="form.allow_to_show_mobile" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('preferred_call_time') }}</label>
                                <input v-model="form.preferred_call_time" type="text" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('space_type') }}</label>
                                <input v-model="form.space_type" type="text" class="form-input">
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('other_facilities') }}</label>
                                <input v-model="form.other_facilities" type="text" class="form-input">
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('image_links') }}</label>
                                <div class="multi">
                                    <input v-model="form.image_link_1" type="text" class="form-input" :placeholder="t('image_link_1')">
                                    <input v-model="form.image_link_2" type="text" class="form-input" :placeholder="t('image_link_2')">
                                    <input v-model="form.image_link_3" type="text" class="form-input" :placeholder="t('image_link_3')">
                                    <input v-model="form.image_link_4" type="text" class="form-input" :placeholder="t('image_link_4')">
                                </div>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('video_links') }}</label>
                                <div class="multi">
                                    <input v-model="form.video_link_1" type="text" class="form-input" :placeholder="t('video_link_1')">
                                    <input v-model="form.video_link_2" type="text" class="form-input" :placeholder="t('video_link_2')">
                                </div>
                            </div>

                            <div class="form-group span-2">
                                <label class="form-label">{{ t('attachment_rate_list') }}</label>
                                <div class="rates-box">
                                    <div v-if="!attachmentRates.length" class="charge-text">{{ t('no_active_attachment_rate') }}</div>
                                    <div v-for="r in attachmentRates" :key="r.AttachmentRateID || (r.AttachmentType + '-' + r.Charge)" class="rate-row">
                                        <span>{{ r.AttachmentType }}</span>
                                        <strong>{{ r.Charge }}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="activeTab === 'billing'" class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('publicity_start_date') }}</label>
                                <input v-model="form.publicity_start_date" type="date" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('publicity_end_date') }}</label>
                                <input v-model="form.publicity_end_date" type="date" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('property_available_from') }}</label>
                                <input v-model="form.property_available_date" type="date" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('public_view_status') }}</label>
                                <select v-model="form.public_view_status" class="form-input">
                                    <option :value="true">{{ t('visible') }}</option>
                                    <option :value="false">{{ t('hidden') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('bill_id') }}</label>
                                <input v-model="form.bill_id" type="number" min="1" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('bill_status') }}</label>
                                <input v-model="form.bill_status" type="text" class="form-input" :placeholder="t('pending_confirmed')">
                            </div>

                            <div class="form-group span-2 callout">
                                <div class="charge-head">
                                    <label class="form-label">{{ t('estimated_publish_charge') }}</label>
                                    <button type="button" class="btn btn-small btn-secondary" @click="calculateCharges" :disabled="saving || chargeLoading">{{ t('calculate') }}</button>
                                </div>
                                <div v-if="chargeLoading" class="charge-text">{{ t('calculating') }}</div>
                                <div v-else-if="chargePreview" class="charge-grid">
                                    <div class="charge-item"><span>{{ t('space') }}</span><strong>{{ chargePreview.space_charge }}</strong></div>
                                    <div class="charge-item"><span>{{ t('rent') }}</span><strong>{{ chargePreview.rent_charge }}</strong></div>
                                    <div class="charge-item"><span>{{ t('attachments') }}</span><strong>{{ chargePreview.attachments_charge }}</strong></div>
                                    <div class="charge-item total"><span>{{ t('total') }}</span><strong>{{ chargePreview.total_charge }}</strong></div>
                                </div>
                                <div v-else class="charge-text">{{ t('set_unit_unit_size_rent_then_calculate') }}</div>
                            </div>
                        </div>

                        <div v-else class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('late_payment_allowed') }}</label>
                                <select v-model="form.is_late_payment_allowed" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('payment_due_date') }}</label>
                                <input v-model="form.payment_due_date" type="date" class="form-input">
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('late_fee_type') }}</label>
                                <select v-model="form.late_fee_type" class="form-input">
                                    <option value="fixed">{{ t('fixed') }}</option>
                                    <option value="percent">{{ t('percent') }}</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ t('late_fee_amount') }}</label>
                                <input v-model="form.late_fee" type="number" min="0" class="form-input">
                            </div>
                        </div>
                    </div>
                    <div class="app-modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeForm">{{ t('cancel') }}</button>
                        <button type="button" class="btn btn-secondary" @click="prevTab" :disabled="isFirstTab">{{ t('previous') }}</button>
                        <button v-if="!isLastTab" type="button" class="btn btn-primary" @click="nextTab" :disabled="saving">{{ t('next') }}</button>
                        <button v-else type="button" class="btn btn-primary" @click="save" :disabled="saving">{{ t('save') }}</button>
                    </div>
                </div>
            </div>

            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', 'toast-' + toast.type]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';

export default {
    name: 'PropertiesPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));

        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const items = ref([]);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const loading = ref(true);
        const saving = ref(false);
        const busyIds = ref({});

        const statusFilter = ref('all');
        const searchQuery = ref('');
        const page = ref(1);
        const perPage = ref(20);
        let searchTimer = null;
        const showColumns = ref(false);
        const showFilters = ref(true);
        const colFilters = ref({
            refNo: '',
            customer: '',
            propertyType: '',
            unit: '',
            rent: '',
            district: '',
            status: '',
        });
        const columns = [
            { key: 'ref_no', labelKey: 'ref_no' },
            { key: 'customer', labelKey: 'customer' },
            { key: 'property_type', labelKey: 'property_type' },
            { key: 'unit', labelKey: 'unit' },
            { key: 'rent', labelKey: 'rent' },
            { key: 'district', labelKey: 'district' },
            { key: 'status', labelKey: 'status' },
            { key: 'actions', labelKey: 'actions', locked: true },
        ];
        const visibleColumns = ref({
            ref_no: true,
            customer: true,
            property_type: true,
            unit: true,
            rent: true,
            district: true,
            status: true,
            actions: true,
        });

        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return columns.reduce((sum, c) => sum + (v[c.key] ? 1 : 0), 0) || 1;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((statusFilter.value || 'all') !== 'all') return true;
            const f = colFilters.value || {};
            return !!(
                (f.refNo || '').trim()
                || (f.customer || '').trim()
                || (f.propertyType || '').trim()
                || (f.unit || '').trim()
                || (f.rent || '').trim()
                || (f.district || '').trim()
                || (f.status || '').trim()
            );
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'all';
            colFilters.value = {
                refNo: '',
                customer: '',
                propertyType: '',
                unit: '',
                rent: '',
                district: '',
                status: '',
            };
            page.value = 1;
            await load();
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            const pages = size > 0 ? Math.ceil(total / size) : 1;
            return Math.max(1, pages);
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const max = 5;
            let start = Math.max(1, current - Math.floor(max / 2));
            let end = start + max - 1;
            if (end > total) {
                end = total;
                start = Math.max(1, end - max + 1);
            }
            const out = [];
            for (let p = start; p <= end; p++) out.push(p);
            return out;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredItems.value.length))
                : `(filtered: ${filteredItems.value.length})`;
            return `${base} ${suffix}`;
        });

        const filteredItems = computed(() => {
            const f = colFilters.value || {};
            const normalize = (v) => (v ?? '').toString().toLowerCase();

            const refQ = normalize(f.refNo).trim();
            const custQ = normalize(f.customer).trim();
            const typeQ = normalize(f.propertyType).trim();
            const unitQ = normalize(f.unit).trim();
            const rentQ = normalize(f.rent).trim();
            const distQ = normalize(f.district).trim();
            const statusQ = (f.status || '').toString().trim();

            return (items.value || []).filter((row) => {
                const ref = normalize(row.ToLetRefNo);
                const customer = normalize(row.CustomerName);
                const mobile = normalize(row.MobileNo);
                const ptype = normalize(row.PropertyTypeName);
                const unit = normalize(row.Unit);
                const rent = normalize(row.Rent);
                const district = normalize(districtLabel(row));
                const statusVal = typeof row.Status === 'number' ? row.Status : parseInt(row.Status || '0', 10);

                if (refQ && !ref.includes(refQ)) return false;
                if (custQ && !(customer.includes(custQ) || mobile.includes(custQ))) return false;
                if (typeQ && !ptype.includes(typeQ)) return false;
                if (unitQ && !unit.includes(unitQ)) return false;
                if (rentQ && !rent.includes(rentQ)) return false;
                if (distQ && !district.includes(distQ)) return false;
                if (statusQ) {
                    if (statusQ === 'draft' && statusVal !== 0) return false;
                    if (statusQ === 'live' && statusVal !== 1) return false;
                    if (statusQ === 'stopped' && statusVal !== 2) return false;
                }
                return true;
            });
        });

        const districts = ref([]);
        const propertyTypes = ref([]);
        const propertyUseTypes = ref([]);
        const thanas = ref([]);
        const areas = ref([]);
        const attachmentRates = ref([]);
        const chargeLoading = ref(false);
        const chargePreview = ref(null);

        const showViewModal = ref(false);
        const viewData = ref(null);
        const showFormModal = ref(false);
        const formMode = ref('create');
        const editingId = ref(null);
        const defaultBodyOverflow = ref('');
        const activeTab = ref('info');
        const tabOrder = ['info', 'location', 'features', 'preferences', 'media', 'billing', 'late'];
        const isFirstTab = computed(() => activeTab.value === tabOrder[0]);
        const isLastTab = computed(() => activeTab.value === tabOrder[tabOrder.length - 1]);

        const setTab = (key) => {
            if (!tabOrder.includes(key)) return;
            activeTab.value = key;
        };
        const nextTab = () => {
            const idx = tabOrder.indexOf(activeTab.value);
            if (idx < 0) return;
            if (idx >= tabOrder.length - 1) return;
            activeTab.value = tabOrder[idx + 1];
        };
        const prevTab = () => {
            const idx = tabOrder.indexOf(activeTab.value);
            if (idx <= 0) return;
            activeTab.value = tabOrder[idx - 1];
        };

        const safeParseJson = (value) => {
            if (!value || typeof value !== 'string') return null;
            try {
                const parsed = JSON.parse(value);
                return parsed && typeof parsed === 'object' ? parsed : null;
            } catch {
                return null;
            }
        };
        const deepMerge = (base, source) => {
            const out = Array.isArray(base) ? [...base] : { ...base };
            if (!source || typeof source !== 'object') return out;
            for (const key of Object.keys(source)) {
                const srcVal = source[key];
                const baseVal = base ? base[key] : undefined;
                if (srcVal && typeof srcVal === 'object' && !Array.isArray(srcVal)) {
                    out[key] = deepMerge(baseVal && typeof baseVal === 'object' ? baseVal : {}, srcVal);
                } else {
                    out[key] = srcVal;
                }
            }
            return out;
        };
        const compactValue = (val) => {
            if (val === null || val === undefined) return undefined;
            if (typeof val === 'boolean') return val ? true : undefined;
            if (typeof val === 'number') return val > 0 ? val : undefined;
            if (typeof val === 'string') {
                const s = val.trim();
                if (s === '') return undefined;
                if (/^-?\\d+(\\.\\d+)?$/.test(s)) {
                    const n = Number(s);
                    return Number.isFinite(n) ? n : s;
                }
                return s;
            }
            if (Array.isArray(val)) {
                const items = val.map(compactValue).filter((x) => x !== undefined);
                return items.length ? items : undefined;
            }
            if (typeof val === 'object') {
                const obj = {};
                for (const [k, v] of Object.entries(val)) {
                    const next = compactValue(v);
                    if (next !== undefined) obj[k] = next;
                }
                return Object.keys(obj).length ? obj : undefined;
            }
            return val;
        };

        const defaultFeatures = () => ({
            rooms_spaces: {
                total_room: '',
                dining_room: false,
                dining_room_size: '',
                drawing_room: false,
                drawing_room_size: '',
                attached_dining_drawing: false,
                attached_dining_drawing_size: '',
                balconies: '',
                floor_type: '',
                floor_level: '',
                total_floor_in_building: '',
            },
            kitchen: {
                has_kitchen: false,
                kitchen_description: '',
                gas_connection_type: '',
            },
            bathroom: {
                attached_bath: '',
                common_bath: '',
                high_commode: '',
                low_commode: '',
                other_facilities: '',
            },
            utilities_services: {
                electricity: '',
                water_wasa: false,
                water_deep_tubewell: false,
                lift: false,
                parking_space: false,
                community_space: false,
                generator_backup_power: false,
                internet_broadband: false,
            },
            security_safety: {
                security_guard: false,
                cctv: false,
                intercom: false,
                fire_safety: false,
            },
        });
        const defaultPreferences = () => ({
            furnishing_status: '',
            furnished_details: '',
            preferred_tenant: {
                family: false,
                small_family: false,
                bachelor: false,
                student_male: false,
                student_female: false,
                service_holder: false,
                any: false,
            },
        });

        const viewFeaturesText = computed(() => {
            const notice = viewData.value?.notice;
            const parsed = safeParseJson(notice?.OwnerFeaturesJson);
            return parsed ? JSON.stringify(parsed, null, 2) : '';
        });
        const viewPreferencesText = computed(() => {
            const notice = viewData.value?.notice;
            const parsed = safeParseJson(notice?.OwnerPreferencesJson);
            return parsed ? JSON.stringify(parsed, null, 2) : '';
        });

        const form = ref({
            customer_id: '',
            property_type_id: '',
            unit: '',
            unit_size: '',
            space_type: '',
            rent: '',
            display_summary: '',
            district_id: '',
            thana_id: '',
            area_id: '',
            address: '',
            latlong: '',
            show_address_to_public: true,
            image_link_1: '',
            image_link_2: '',
            image_link_3: '',
            image_link_4: '',
            video_link_1: '',
            video_link_2: '',
            other_facilities: '',
            is_foreigner_allow: true,
            preferred_call_time: '',
            allow_to_show_mobile: true,
            publicity_start_date: '',
            publicity_end_date: '',
            property_available_date: '',
            bill_id: '',
            bill_status: '',
            public_view_status: false,
            is_late_payment_allowed: false,
            payment_due_date: '',
            late_fee_type: 'fixed',
            late_fee: '',
            features: defaultFeatures(),
            preferences: defaultPreferences(),
            property_use_type_ids: [],
            status: 'draft',
        });

        const toasts = ref([]);
        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };
        const removeToast = (id) => {
            toasts.value = toasts.value.filter((x) => x.id !== id);
        };

        const districtOptionLabel = (d) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (d.District_Name_BN || d.District_Name) : (d.District_Name || d.District_Name_BN);
        };

        const districtLabel = (row) => {
            const isBn = adminLang?.value === 'bn';
            const bn = row.District_Name_BN || row.District_Name || '';
            const en = row.District_Name || row.District_Name_BN || '';
            return isBn ? (bn || '-') : (en || '-');
        };

        const propertyTypeLabel = (pt) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (pt.PropertyTypeName_BN || pt.PropertyTypeName) : (pt.PropertyTypeName || pt.PropertyTypeName_BN);
        };

        const propertyUseTypeLabel = (u) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (u.PropertyUseTypeName_BN || u.PropertyUseTypeName) : (u.PropertyUseTypeName || u.PropertyUseTypeName_BN);
        };

        const useTypeLabel = (u) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (u.PropertyUseTypeName_BN || u.PropertyUseTypeName) : (u.PropertyUseTypeName || u.PropertyUseTypeName_BN);
        };

        const thanaLabel = (x) => {
            const isBn = adminLang?.value === 'bn';
            return isBn
                ? (x.ThanaName_BN || x.Thana_Name_BN || x.ThanaName || x.Thana_Name)
                : (x.ThanaName || x.Thana_Name || x.ThanaName_BN || x.Thana_Name_BN);
        };

        const areaLabel = (x) => {
            const isBn = adminLang?.value === 'bn';
            return isBn
                ? (x.AreaName_BN || x.Area_Name_BN || x.AreaName || x.Area_Name)
                : (x.AreaName || x.Area_Name || x.AreaName_BN || x.Area_Name_BN);
        };

        const statusLabel = (status) => {
            const v = typeof status === 'number' ? status : parseInt(status || '0', 10);
            if (v === 1) return t('live');
            if (v === 2) return t('stopped');
            return t('draft');
        };

        const badgeClass = (status) => {
            const v = typeof status === 'number' ? status : parseInt(status || '0', 10);
            if (v === 1) return 'badge-success';
            if (v === 2) return 'badge-warning';
            return 'badge-muted';
        };

        const nextStatusLabel = (status) => {
            const v = typeof status === 'number' ? status : parseInt(status || '0', 10);
            if (v === 1) return t('stop');
            if (v === 2) return t('draft');
            return t('publish');
        };

        const nextStatusValue = (status) => {
            const v = typeof status === 'number' ? status : parseInt(status || '0', 10);
            if (v === 1) return 2;
            if (v === 2) return 0;
            return 1;
        };

        const loadDistricts = async () => {
            try {
                const res = await window.axios.get('/api/v1/districts', { params: { lang: langValue() } });
                districts.value = res.data?.data?.items || res.data?.data || [];
            } catch {
                districts.value = [];
            }
        };

        const loadPropertyTypes = async () => {
            try {
                const res = await window.axios.get('/api/v1/property-types', { params: { lang: langValue() } });
                propertyTypes.value = res.data?.data?.items || res.data?.data || [];
            } catch {
                propertyTypes.value = [];
            }
        };

        const loadAttachmentRates = async () => {
            try {
                const res = await window.axios.get('/api/v1/attachment-rates', { params: { lang: langValue() } });
                attachmentRates.value = res.data?.data?.items || res.data?.data || [];
            } catch {
                attachmentRates.value = [];
            }
        };

        const normalizeDateInput = (v) => {
            if (!v) return '';
            const s = String(v);
            return s.length >= 10 ? s.slice(0, 10) : '';
        };

        const filledCount = (list) => list.filter((x) => !!String(x || '').trim()).length;

        const loadUseTypes = async () => {
            const propertyTypeId = parseInt(form.value.property_type_id || '0', 10);
            if (!propertyTypeId) {
                propertyUseTypes.value = [];
                form.value.property_use_type_ids = [];
                return;
            }
            try {
                const res = await window.axios.get('/api/v1/property-use-types', { params: { property_type_id: propertyTypeId, lang: langValue() } });
                propertyUseTypes.value = res.data?.data?.items || res.data?.data || [];
            } catch {
                propertyUseTypes.value = [];
            }
        };

        const onDistrictChange = async (preserveCurrent = false) => {
            if (!preserveCurrent) {
                form.value.thana_id = '';
                form.value.area_id = '';
                areas.value = [];
            }
            const districtId = parseInt(form.value.district_id || '0', 10);
            if (!districtId) {
                thanas.value = [];
                return;
            }
            try {
                const res = await window.axios.get('/api/v1/thanas', { params: { district_id: districtId, lang: langValue() } });
                const raw = res.data?.data?.items || res.data?.data || [];
                thanas.value = Array.isArray(raw)
                    ? raw
                        .map((x) => ({
                            ...x,
                            ThanaID: x.ThanaID ?? x.Thana_ID ?? x.ThanaId ?? x.thana_id ?? null,
                            ThanaName: x.ThanaName ?? x.Thana_Name ?? null,
                            ThanaName_BN: x.ThanaName_BN ?? x.Thana_Name_BN ?? null,
                        }))
                        .filter((x) => x.ThanaID)
                    : [];
            } catch {
                thanas.value = [];
            }
        };

        const onThanaChange = async (preserveCurrent = false) => {
            if (!preserveCurrent) {
                form.value.area_id = '';
            }
            const thanaId = parseInt(form.value.thana_id || '0', 10);
            if (!thanaId) {
                areas.value = [];
                return;
            }
            try {
                const res = await window.axios.get('/api/v1/areas', { params: { thana_id: thanaId, lang: langValue() } });
                const raw = res.data?.data?.items || res.data?.data || [];
                areas.value = Array.isArray(raw)
                    ? raw
                        .map((x) => ({
                            ...x,
                            AreaID: x.AreaID ?? x.Area_ID ?? x.AreaId ?? x.area_id ?? null,
                            AreaName: x.AreaName ?? x.Area_Name ?? null,
                            AreaName_BN: x.AreaName_BN ?? x.Area_Name_BN ?? null,
                        }))
                        .filter((x) => x.AreaID)
                    : [];
            } catch {
                areas.value = [];
            }
        };

        const calculateCharges = async () => {
            const propertyTypeId = parseInt(form.value.property_type_id || '0', 10);
            const rent = parseInt(form.value.rent || '0', 10);
            const unitSize = parseFloat(form.value.unit_size || '0');
            const unit = String(form.value.unit || '').trim();
            if (!propertyTypeId || !unit || !unitSize || rent < 0) {
                addToast('error', t('fill_required_for_charge_calc'));
                return;
            }

            chargeLoading.value = true;
            chargePreview.value = null;
            try {
                const photoQty = filledCount([form.value.image_link_1, form.value.image_link_2, form.value.image_link_3, form.value.image_link_4]);
                const videoQty = filledCount([form.value.video_link_1, form.value.video_link_2]);
                const res = await window.axios.post('/api/v1/owner-charge/calculate', {
                    property_type_id: propertyTypeId,
                    unit,
                    unit_size: unitSize,
                    rent,
                    photo_qty: photoQty,
                    video_qty: videoQty,
                    lang: langValue(),
                });
                chargePreview.value = res.data?.data?.charges || null;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('charge_calculation_failed'));
            } finally {
                chargeLoading.value = false;
            }
        };

        const load = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/properties', {
                    params: {
                        q: searchQuery.value || '',
                        status: statusFilter.value,
                        page: page.value,
                        per_page: perPage.value,
                        lang: langValue(),
                    },
                });
                items.value = res.data?.data || [];
                pagination.value = res.data?.pagination || { page: 1, per_page: 20, total: 0 };
                page.value = pagination.value?.page || page.value;
                perPage.value = pagination.value?.per_page || perPage.value;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                page.value = 1;
                load();
            }, 350);
        };

        const goPage = async (p) => {
            const next = Math.min(Math.max(1, Number(p) || 1), totalPages.value);
            if (next === page.value) return;
            page.value = next;
            await load();
        };
        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await load();
        };
        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await load();
        };
        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await load();
        };
        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await load();
        };

        const openView = async (id) => {
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.get(`/admin/api/properties/${id}`, { params: { lang: langValue() } });
                viewData.value = res.data?.data || null;
                showViewModal.value = true;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        const closeView = () => {
            showViewModal.value = false;
            viewData.value = null;
        };

        const resetForm = () => {
            form.value = {
                customer_id: '',
                property_type_id: '',
                unit: '',
                unit_size: '',
                space_type: '',
                rent: '',
                display_summary: '',
                district_id: '',
                thana_id: '',
                area_id: '',
                address: '',
                latlong: '',
                show_address_to_public: true,
                image_link_1: '',
                image_link_2: '',
                image_link_3: '',
                image_link_4: '',
                video_link_1: '',
                video_link_2: '',
                other_facilities: '',
                is_foreigner_allow: true,
                preferred_call_time: '',
                allow_to_show_mobile: true,
                publicity_start_date: '',
                publicity_end_date: '',
                property_available_date: '',
                bill_id: '',
                bill_status: '',
                public_view_status: false,
                is_late_payment_allowed: false,
                payment_due_date: '',
                late_fee_type: 'fixed',
                late_fee: '',
                features: defaultFeatures(),
                preferences: defaultPreferences(),
                property_use_type_ids: [],
                status: 'draft',
            };
            thanas.value = [];
            areas.value = [];
            propertyUseTypes.value = [];
            chargePreview.value = null;
            editingId.value = null;
            activeTab.value = tabOrder[0];
        };

        const openCreate = async () => {
            formMode.value = 'create';
            resetForm();
            showFormModal.value = true;
        };

        const openEdit = async (id) => {
            formMode.value = 'edit';
            resetForm();
            editingId.value = id;
            saving.value = true;
            try {
                const res = await window.axios.get(`/admin/api/properties/${id}`, { params: { lang: langValue() } });
                const notice = res.data?.data?.notice;
                const useTypes = res.data?.data?.use_types || [];
                if (!notice) throw new Error('Not found');

                form.value.customer_id = notice.CustomerID ? String(notice.CustomerID) : '';
                form.value.property_type_id = notice.PropertyTypeID ? String(notice.PropertyTypeID) : '';
                form.value.unit = notice.Unit || '';
                form.value.unit_size = notice.UnitSize ?? '';
                form.value.space_type = notice.SpaceType || '';
                form.value.rent = notice.Rent ?? '';
                form.value.display_summary = notice.DisplaySummary || '';
                form.value.district_id = notice.DistrictID ? String(notice.DistrictID) : '';
                form.value.thana_id = notice.ThanaID ? String(notice.ThanaID) : '';
                form.value.area_id = notice.AreaID ? String(notice.AreaID) : '';
                form.value.address = notice.Address || '';
                form.value.latlong = notice.Latlong || '';
                form.value.show_address_to_public = notice.ShowAddressToPublic === null ? true : !!notice.ShowAddressToPublic;
                form.value.image_link_1 = notice.IamgeLink1 || '';
                form.value.image_link_2 = notice.ImageLink2 || '';
                form.value.image_link_3 = notice.ImageLink3 || '';
                form.value.image_link_4 = notice.ImageLink4 || '';
                form.value.video_link_1 = notice.VideoLink1 || '';
                form.value.video_link_2 = notice.VideoLink2 || '';
                form.value.other_facilities = notice.OtherFacilities || '';
                form.value.is_foreigner_allow = notice.IsForeignerAllow === null ? true : !!notice.IsForeignerAllow;
                form.value.preferred_call_time = notice.PreferredCallTime || '';
                form.value.allow_to_show_mobile = notice.AllowToShowMobile === null ? true : !!notice.AllowToShowMobile;
                form.value.publicity_start_date = normalizeDateInput(notice.PublicityStartDate);
                form.value.publicity_end_date = normalizeDateInput(notice.PublicityEndDate);
                form.value.property_available_date = normalizeDateInput(notice.PropertyAvailableDate);
                form.value.bill_id = notice.BillID ?? '';
                form.value.bill_status = notice.BillStatus || '';
                form.value.public_view_status = notice.PublicViewStatus === null ? false : !!notice.PublicViewStatus;
                form.value.is_late_payment_allowed = notice.IsLatePaymentAllowed === null ? false : !!notice.IsLatePaymentAllowed;
                form.value.payment_due_date = normalizeDateInput(notice.PaymentDueDate);
                form.value.late_fee_type = notice.LateFeeType || 'fixed';
                form.value.late_fee = notice.LateFee ?? '';
                form.value.features = deepMerge(defaultFeatures(), safeParseJson(notice.OwnerFeaturesJson) || {});
                form.value.preferences = deepMerge(defaultPreferences(), safeParseJson(notice.OwnerPreferencesJson) || {});
                form.value.property_use_type_ids = useTypes.map((u) => u.PropertyUseTypeID);
                form.value.status = notice.Status === 1 ? 'live' : (notice.Status === 2 ? 'stopped' : 'draft');

                await loadUseTypes();
                if (form.value.district_id) await onDistrictChange(true);
                if (form.value.thana_id) await onThanaChange(true);

                showFormModal.value = true;
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                saving.value = false;
            }
        };

        const closeForm = () => {
            showFormModal.value = false;
        };

        watch(
            () => [showViewModal.value, showFormModal.value],
            ([viewOpen, formOpen]) => {
                const anyOpen = !!viewOpen || !!formOpen;
                if (typeof document === 'undefined') return;
                if (defaultBodyOverflow.value === '') {
                    defaultBodyOverflow.value = document.body.style.overflow || '';
                }
                document.body.style.overflow = anyOpen ? 'hidden' : defaultBodyOverflow.value;
            },
            { immediate: true }
        );

        const save = async () => {
            saving.value = true;
            try {
                const payload = {
                    customer_id: parseInt(form.value.customer_id || '0', 10) || null,
                    property_type_id: parseInt(form.value.property_type_id || '0', 10) || null,
                    unit: form.value.unit || null,
                    unit_size: form.value.unit_size === '' ? null : parseFloat(form.value.unit_size),
                    space_type: form.value.space_type || null,
                    rent: form.value.rent === '' ? null : parseInt(form.value.rent || '0', 10),
                    display_summary: form.value.display_summary || null,
                    district_id: form.value.district_id ? parseInt(form.value.district_id, 10) : null,
                    thana_id: form.value.thana_id ? parseInt(form.value.thana_id, 10) : null,
                    area_id: form.value.area_id ? parseInt(form.value.area_id, 10) : null,
                    address: form.value.address || null,
                    latlong: form.value.latlong || null,
                    show_address_to_public: !!form.value.show_address_to_public,
                    image_link_1: form.value.image_link_1 || null,
                    image_link_2: form.value.image_link_2 || null,
                    image_link_3: form.value.image_link_3 || null,
                    image_link_4: form.value.image_link_4 || null,
                    video_link_1: form.value.video_link_1 || null,
                    video_link_2: form.value.video_link_2 || null,
                    other_facilities: form.value.other_facilities || null,
                    is_foreigner_allow: !!form.value.is_foreigner_allow,
                    preferred_call_time: form.value.preferred_call_time || null,
                    allow_to_show_mobile: !!form.value.allow_to_show_mobile,
                    publicity_start_date: form.value.publicity_start_date || null,
                    publicity_end_date: form.value.publicity_end_date || null,
                    property_available_date: form.value.property_available_date || null,
                    bill_id: parseInt(form.value.bill_id || '0', 10) || null,
                    bill_status: form.value.bill_status || null,
                    public_view_status: !!form.value.public_view_status,
                    is_late_payment_allowed: !!form.value.is_late_payment_allowed,
                    payment_due_date: form.value.payment_due_date || null,
                    late_fee_type: form.value.late_fee_type || null,
                    late_fee: form.value.late_fee === '' ? null : parseInt(form.value.late_fee || '0', 10),
                    property_use_type_ids: Array.isArray(form.value.property_use_type_ids) ? form.value.property_use_type_ids : [],
                    features: compactValue(form.value.features) || null,
                    preferences: compactValue(form.value.preferences) || null,
                    status: form.value.status || 'draft',
                    lang: langValue(),
                };

                if (formMode.value === 'create') {
                    const res = await window.axios.post('/admin/api/properties', payload);
                    addToast('success', res.data?.message || t('success'));
                } else {
                    delete payload.customer_id;
                    const res = await window.axios.put(`/admin/api/properties/${editingId.value}`, payload);
                    addToast('success', res.data?.message || t('success'));
                }
                showFormModal.value = false;
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                saving.value = false;
            }
        };

        const toggleStatus = async (row) => {
            const id = row.ToLetNoticeID;
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.put(`/admin/api/properties/${id}/status`, { status: nextStatusValue(row.Status), lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        const remove = async (id) => {
            if (!window.confirm(t('confirm_delete'))) return;
            busyIds.value = { ...busyIds.value, [id]: true };
            try {
                const res = await window.axios.delete(`/admin/api/properties/${id}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [id]: false };
            }
        };

        onMounted(async () => {
            await loadDistricts();
            await loadPropertyTypes();
            await loadAttachmentRates();
            await load();
        });

        watch(statusFilter, async () => {
            page.value = 1;
            await load();
        });

        watch(perPage, async () => {
            page.value = 1;
            await load();
        });

        return {
            t,
            items,
            pagination,
            loading,
            saving,
            busyIds,
            statusFilter,
            searchQuery,
            page,
            perPage,
            columns,
            visibleColumns,
            showColumns,
            toggleColumns,
            visibleColumnCount,
            showFilters,
            colFilters,
            hasAnyFilter,
            toggleFilters,
            clearFilters,
            totalPages,
            pageButtons,
            showingText,
            filteredItems,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            debouncedSearch,
            districts,
            propertyTypes,
            propertyUseTypes,
            thanas,
            areas,
            attachmentRates,
            chargeLoading,
            chargePreview,
            districtLabel,
            districtOptionLabel,
            propertyTypeLabel,
            propertyUseTypeLabel,
            useTypeLabel,
            thanaLabel,
            areaLabel,
            statusLabel,
            badgeClass,
            nextStatusLabel,
            showViewModal,
            viewData,
            showFormModal,
            formMode,
            form,
            load,
            loadUseTypes,
            calculateCharges,
            onDistrictChange,
            onThanaChange,
            openView,
            closeView,
            openCreate,
            openEdit,
            closeForm,
            save,
            toggleStatus,
            remove,
            toasts,
            activeTab,
            setTab,
            nextTab,
            prevTab,
            isFirstTab,
            isLastTab,
            removeToast,
            viewFeaturesText,
            viewPreferencesText,
        };
    },
};
</script>

<style scoped>
.page {
    display: flex;
    flex-direction: column;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
}

.page-subtitle {
    margin: 6px 0 0 0;
    color: #64748b;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.card {
    margin-top: 18px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.datatable-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    padding: 10px 10px;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.12);
    z-index: 40;
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 800;
    color: #0f172a;
    font-size: 12px;
}

.columns-item:hover {
    background: rgba(241, 245, 249, 0.8);
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
}

.data-table thead {
    background: #0b2c3d;
}

.data-table th {
    text-align: left;
    padding: 10px 8px;
    color: #ffffff;
    font-weight: 900;
    font-size: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.th-input-right {
    text-align: right;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.cell-main {
    font-weight: 600;
}

.cell-sub {
    color: #64748b;
    font-size: 12px;
    margin-top: 2px;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.actions .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    line-height: 1;
    width: 34px;
    height: 32px;
    padding: 0;
    border-radius: 10px;
}

.actions .btn svg {
    flex: 0 0 auto;
}

.col-rent {
    text-align: right;
}

.btn {
    padding: 10px 16px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.btn-small {
    padding: 8px 10px;
    font-size: 12px;
    border-radius: 10px;
}

.btn-primary {
    background: #4A90E2;
    color: #fff;
}

.btn-secondary {
    background: #ffffff;
    border-color: #e2e8f0;
    color: #1e293b;
}

.btn-danger {
    background: #ef4444;
    color: #fff;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
}

.badge-success {
    background: rgba(34, 197, 94, 0.15);
    color: #15803d;
}

.badge-warning {
    background: rgba(245, 158, 11, 0.18);
    color: #b45309;
}

.badge-muted {
    background: rgba(148, 163, 184, 0.18);
    color: #475569;
}

.loading {
    padding: 18px 0;
    color: #64748b;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 2px 10px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% { background-position: 0% 0%; }
    100% { background-position: -200% 0%; }
}

.empty {
    text-align: center;
    color: #94a3b8;
    padding: 20px;
}

.app-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.65);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 20px;
    z-index: 1100;
    overflow: auto;
}

.app-modal {
    width: 100%;
    max-width: 720px;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
}

.app-modal-large {
    max-width: 980px;
}

.app-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid #eef2f7;
}

.app-modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
}

.app-modal-close {
    background: transparent;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: #64748b;
}

.app-modal-body {
    padding: 20px;
    overflow: auto;
    flex: 1;
}

.app-modal-actions {
    padding: 16px 20px;
    border-top: 1px solid #eef2f7;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.detail {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
}

.span-2 {
    grid-column: span 2;
}

.detail-label {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.detail-value {
    margin-top: 6px;
    color: #0f172a;
    font-weight: 600;
    font-size: 14px;
    word-break: break-word;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}

.form-input {
    padding: 10px 12px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
}

.advanced {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    background: #f8fafc;
}

.advanced summary {
    cursor: pointer;
    font-weight: 700;
    color: #1e293b;
}

.advanced-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.multi {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
}

.callout {
    border: 1px solid #dbeafe;
    border-radius: 12px;
    padding: 12px;
    background: #f8fbff;
}

.step-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eef2f7;
}

.step-tab {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #334155;
    padding: 8px 10px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
}

.step-tab.active {
    background: #0ea5e9;
    border-color: #0ea5e9;
    color: #fff;
}

.charge-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.json-box {
    background: #0b1220;
    color: #e2e8f0;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.5;
    overflow: auto;
    max-height: 240px;
    margin: 0;
}

.inner-grid {
    margin-top: 10px;
}

.charge-grid {
    margin-top: 10px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.charge-item {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 8px 10px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
}

.charge-item.total {
    border-color: #bfdbfe;
    background: #eff6ff;
    font-weight: 700;
}

.charge-text {
    margin-top: 8px;
    color: #64748b;
    font-size: 13px;
}

.rates-box {
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    padding: 10px;
    background: #fff;
}

.rate-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
}

.rate-row:last-child {
    border-bottom: none;
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.toast {
    background: white;
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 320px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-left: 4px solid;
}

.toast-success {
    border-left-color: #10b981;
}

.toast-error {
    border-left-color: #ef4444;
}

.toast-message {
    margin: 0;
    color: #1a202c;
    font-weight: 600;
    font-size: 14px;
}

.toast-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #64748b;
    padding: 4px;
}

@media (max-width: 720px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    .form-grid,
    .details-grid,
    .advanced-grid {
        grid-template-columns: 1fr;
    }
    .span-2 {
        grid-column: auto;
    }
}
</style>
